import { formatMoney, formatUsPhone, type Order } from '@mevaser/shared'

const CHANNEL_LABEL: Record<string, string> = {
  web: 'Website',
  pos: 'In store',
  kiosk: 'Kiosk',
  phone: 'Phone',
}

const STORE_NAME = 'Mevaser Tech'
const STORE_SITE = 'mevasertech.net'

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Pure: builds the full receipt document for an order. */
export function renderReceiptHtml(order: Order): string {
  const e = escapeHtml
  const placed = new Date(order.createdAt).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  const customerName = order.customerName || order.buyerName || null
  const customerPhone = order.customerName ? order.customerPhone : order.buyerPhone
  const customerHtml = customerName
    ? `<p><strong>${e(customerName)}</strong>${
        customerPhone ? `<br />${e(formatUsPhone(customerPhone))}` : ''
      }</p>`
    : '<p>Walk-in customer</p>'

  const addr = order.shippingAddress
  const addressHtml =
    order.fulfillmentMethod === 'shipping' && addr
      ? `<p class="muted">Ship to:<br />${e(addr.line1)}<br />${
          addr.line2 ? `${e(addr.line2)}<br />` : ''
        }${e(addr.city)}, ${e(addr.state)} ${e(addr.postalCode)}</p>`
      : ''

  const itemRows = order.items
    .map(
      (item) => `
      <tr>
        <td>
          ${e(item.productName)}
          <div class="muted small">${e(item.sku)}${item.imei ? ` · IMEI ${e(item.imei)}` : ''}${
            item.returnedQty > 0 ? ` · Returned ×${item.returnedQty}` : ''
          }</div>
        </td>
        <td class="num">${item.qty}</td>
        <td class="num">${e(formatMoney(item.unitPriceCents))}</td>
        <td class="num">${e(formatMoney(item.lineTotalCents))}</td>
      </tr>`,
    )
    .join('')

  const row = (label: string, value: string, cls = '') =>
    `<tr class="${cls}"><td>${e(label)}</td><td class="num">${e(value)}</td></tr>`

  const totals: string[] = [row('Subtotal', formatMoney(order.subtotalCents))]
  if (order.discountCents > 0) {
    totals.push(
      row(
        order.couponCode ? `Discount (${order.couponCode})` : 'Discount',
        `−${formatMoney(order.discountCents)}`,
      ),
    )
  }
  if (order.shippingCents > 0) totals.push(row('Shipping', formatMoney(order.shippingCents)))
  if (order.taxCents > 0 || order.taxExemptApplied) {
    totals.push(row('Tax', order.taxExemptApplied ? 'Exempt' : formatMoney(order.taxCents)))
  }
  totals.push(row('Total', formatMoney(order.totalCents), 'total'))
  for (const credit of order.credits ?? []) {
    totals.push(
      row(
        credit.status === 'pending' ? `${credit.label} (held)` : credit.label,
        `−${formatMoney(credit.amountCents)}`,
      ),
    )
  }
  totals.push(row('Paid', formatMoney(order.paidCents)))
  if (order.refundedCents > 0) totals.push(row('Refunded', formatMoney(order.refundedCents)))
  const fees = order.returns.reduce((sum, r) => sum + r.feeCents, 0)
  if (fees > 0) totals.push(row('Restocking fees kept', formatMoney(fees)))
  if (order.balanceDueCents > 0) {
    totals.push(row('Balance due', formatMoney(order.balanceDueCents), 'total'))
  }

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Receipt ${e(order.orderNumber)}</title>
<style>
  @page { size: letter; margin: 0.5in }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
         font-size: 13px; color: #000; }
  h1 { font-size: 22px; margin: 0; }
  h2 { font-size: 14px; margin: 0 0 2px; text-transform: uppercase; letter-spacing: 1px; }
  p { margin: 0 0 6px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 14px; }
  .muted { color: #555; }
  .small { font-size: 11px; }
  table { width: 100%; border-collapse: collapse; }
  .items th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;
              border-bottom: 1px solid #000; padding: 4px 6px; }
  .items td { padding: 6px; border-bottom: 1px solid #ddd; vertical-align: top; }
  .items th.num, .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .totals { width: 55%; margin-left: auto; margin-top: 14px; }
  .totals td { padding: 3px 6px; }
  .totals tr.total td { font-weight: 700; border-top: 1px solid #000; font-size: 14px; }
  .foot { margin-top: 28px; text-align: center; }
  tr { break-inside: avoid; }
</style>
</head>
<body>
  <div class="head">
    <div>
      <h1>${e(STORE_NAME)}</h1>
      <p class="muted">${e(STORE_SITE)}</p>
    </div>
    <div style="text-align:right">
      <h2>Receipt</h2>
      <p><strong>${e(order.orderNumber)}</strong></p>
      <p class="muted">${e(placed)}<br />${e(CHANNEL_LABEL[order.channel] ?? order.channel)}</p>
    </div>
  </div>
  <div>${customerHtml}${addressHtml}</div>
  <table class="items">
    <thead><tr><th>Item</th><th class="num">Qty</th><th class="num">Unit</th><th class="num">Total</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <table class="totals"><tbody>${totals.join('')}</tbody></table>
  <p class="foot">Thank you for your business.</p>
</body>
</html>`
}

/**
 * Prints an order receipt via a hidden same-origin iframe — the same approach
 * as label-print.ts, which avoids popup blockers.
 */
export function printOrderReceipt(order: Order): void {
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)

  const cleanup = () => {
    iframe.remove()
  }

  const doc = iframe.contentDocument
  if (!doc) {
    cleanup()
    return
  }
  doc.open()
  doc.write(renderReceiptHtml(order))
  doc.close()

  iframe.contentWindow?.addEventListener('afterprint', cleanup)
  setTimeout(cleanup, 60_000)

  requestAnimationFrame(() => {
    iframe.contentWindow?.focus()
    iframe.contentWindow?.print()
  })
}
